import type { DesignZone } from '@/core/texture/textureFactory';

export type TextStyleKey = 'team' | 'name' | 'number';

export type TextStyle = {
  x: number;
  y: number;
  fontSize: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  rotation: number;
  visible: boolean;
};

export type Personalisation = {
  teamName: string;
  playerName: string;
  number: string;
  teamStyle?: Partial<TextStyle>;
  nameStyle?: Partial<TextStyle>;
  numberStyle?: Partial<TextStyle>;
};

export const DEFAULT_TEXT_STYLES: Record<TextStyleKey, TextStyle> = {
  team: { x: 1000, y: 1330, fontSize: 72, fill: '#ffffff', stroke: '#0a1a4a', strokeWidth: 5, rotation: 0, visible: true },
  name: { x: 1000, y: 1450, fontSize: 110, fill: '#ffffff', stroke: '#0a1a4a', strokeWidth: 7, rotation: 0, visible: true },
  number: { x: 1000, y: 1650, fontSize: 240, fill: '#ffffff', stroke: '#0a1a4a', strokeWidth: 9, rotation: 0, visible: true },
};

export function resolveTextStyle(key: TextStyleKey, p: Personalisation): TextStyle {
  const base = DEFAULT_TEXT_STYLES[key];
  const patch =
    key === 'team' ? p.teamStyle : key === 'name' ? p.nameStyle : p.numberStyle;
  return { ...base, ...patch };
}

export type GarmentSize = {
  label: string;
  chestCm: number;
  lengthCm: number;
  sleeveCm: number;
};

export type ArtworkSide = 'front' | 'back';

export type ArtworkPositionId =
  | 'front-top-left'
  | 'front-top-center'
  | 'front-top-right'
  | 'front-center'
  | 'front-bottom'
  | 'back-top'
  | 'back-middle'
  | 'back-bottom'
  | 'back-left'
  | 'back-right'
  | 'sleeve-left'
  | 'sleeve-right'
  | 'sleeve-left-top'
  | 'sleeve-left-bottom'
  | 'sleeve-right-top'
  | 'sleeve-right-bottom'
  | 'custom';

export type Artwork = {
  id: string;
  name: string;
  dataUrl: string;
  side: ArtworkSide;
  position: ArtworkPositionId;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  opacity: number;
  /** when true, also draw a mirrored copy on the opposite sleeve */
  mirrorSleeves: boolean;
};

/** Garment region a pattern layer is clipped to */
export type PatternRegionId =
  | 'front'
  | 'back'
  | 'sleeve-left'
  | 'sleeve-right'
  | 'collar'
  | 'cuff'
  | 'hem'
  | 'placket'
  | `zone:${string}`;

export type PatternLayer = {
  id: string;
  region: PatternRegionId;
  /** SVG template URL (UV-mapped) or image dataURL */
  src: string;
  /** svg = full UV design clipped to region; image = drawn to cover the region */
  kind: 'svg' | 'image';
  name: string;
  opacity: number;
  /** 0.25–2, relative fit inside the region */
  scale: number;
  /** degrees */
  rotation: number;
  /** -1..1 within region */
  offsetX: number;
  offsetY: number;
  visible: boolean;
  /** original SVG colour (normalized hex) → replacement hex */
  colorOverrides?: Record<string, string>;
};

/** Pull solid colours out of SVG markup (fill / stroke / stop-color). */
export function extractSvgColors(svg: string): string[] {
  const found = new Set<string>();
  const add = (raw: string) => {
    const v = raw.trim().replace(/^["']|["']$/g, '');
    if (!v || /^(none|transparent|inherit|currentcolor|url\()/i.test(v)) return;
    const hex = normalizeHex(v);
    if (hex) found.add(hex);
  };
  const attr = /(?:fill|stroke|stop-color)\s*=\s*["']([^"']+)["']/gi;
  const style = /(?:fill|stroke|stop-color)\s*:\s*([^;"'\s]+)/gi;
  let m: RegExpExecArray | null;
  while ((m = attr.exec(svg))) add(m[1]!);
  while ((m = style.exec(svg))) add(m[1]!);
  return [...found];
}

/** Normalize #abc / #aabbcc / rgb() → lowercase #rrggbb, or null. */
export function normalizeHex(color: string): string | null {
  const c = color.trim().replace(/^["']|["']$/g, '');
  const short = /^#([0-9a-fA-F]{3})$/.exec(c);
  if (short) {
    const [r, g, b] = short[1]!.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{6}$/.test(c)) return c.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(c)) return c.slice(0, 7).toLowerCase();
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(c);
  if (rgb) {
    const h = (n: string) => Number(n).toString(16).padStart(2, '0');
    return `#${h(rgb[1]!)}${h(rgb[2]!)}${h(rgb[3]!)}`.toLowerCase();
  }
  return null;
}

function colorVariants(from: string): string[] {
  const n = normalizeHex(from) ?? from;
  const set = new Set<string>([n, n.toUpperCase(), from]);
  if (/^#[0-9a-f]{6}$/i.test(n)) {
    const r = n[1]!;
    const g = n[2]!;
    const b = n[3]!;
    // shorthand only when each pair matches
    if (r === n[4] && g === n[5] && b === n[6]) {
      set.add(`#${r}${g}${b}`);
      set.add(`#${r}${g}${b}`.toUpperCase());
    }
    const rgb = parseInt(n.slice(1), 16);
    const rr = (rgb >> 16) & 255;
    const gg = (rgb >> 8) & 255;
    const bb = rgb & 255;
    set.add(`rgb(${rr},${gg},${bb})`);
    set.add(`rgb(${rr}, ${gg}, ${bb})`);
  }
  return [...set];
}

/** Replace original SVG colours with overrides (keys = original normalized hex). */
export function applySvgColorOverrides(svg: string, overrides: Record<string, string> | undefined): string {
  if (!overrides || !Object.keys(overrides).length) return svg;
  let out = svg;
  for (const [from, to] of Object.entries(overrides)) {
    if (!to || normalizeHex(to) === normalizeHex(from)) continue;
    const target = normalizeHex(to) ?? to;
    for (const v of colorVariants(from)) {
      if (!v) continue;
      const re = new RegExp(v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      out = out.replace(re, target);
    }
  }
  return out;
}

export function isSvgDataUrl(src: string): boolean {
  return src.startsWith('data:image/svg+xml') || /^https?:\/\/\S+\.svg(\?|#|$)/i.test(src.trim());
}

export function decodeSvgDataUrl(src: string): string | null {
  if (src.trimStart().startsWith('<svg') || src.trimStart().startsWith('<?xml')) return src;
  if (!src.startsWith('data:image/svg+xml')) return null;
  const comma = src.indexOf(',');
  if (comma < 0) return null;
  const meta = src.slice(0, comma);
  const data = src.slice(comma + 1);
  if (meta.includes('base64')) {
    try {
      const bin = atob(data);
      const bytes = Uint8Array.from(bin, ch => ch.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch {
      return null;
    }
  }
  try {
    return decodeURIComponent(data);
  } catch {
    return null;
  }
}

export function encodeSvgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export type SerializedDesign = {
  v: 3;
  productId: string;
  templateId: string | null;
  colors: DesignZone[];
  personalisation: Personalisation;
  size: GarmentSize;
  artworks: Artwork[];
  /** multi-region pattern layers (optional) */
  layers?: PatternLayer[];
  savedAt: string;
};

export const DEFAULT_SIZE: GarmentSize = {
  label: 'M',
  chestCm: 100,
  lengthCm: 70,
  sleeveCm: 22,
};

export function serializeDesign(input: {
  productId: string;
  templateId: string | null;
  zones: DesignZone[];
  personalisation: Personalisation;
  size: GarmentSize;
  artworks: Artwork[];
  layers?: PatternLayer[];
}): SerializedDesign {
  return {
    v: 3,
    productId: input.productId,
    templateId: input.templateId,
    colors: input.zones.map(z => ({ id: z.id, name: z.name, hex: z.hex })),
    personalisation: { ...input.personalisation },
    size: { ...input.size },
    artworks: input.artworks.map(a => ({ ...a })),
    layers: input.layers?.map(l => ({ ...l })),
    savedAt: new Date().toISOString(),
  };
}

function parseArtwork(raw: unknown): Artwork | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Partial<Artwork>;
  if (typeof o.id !== 'string' || typeof o.dataUrl !== 'string') return null;
  return {
    id: o.id,
    name: typeof o.name === 'string' ? o.name : 'Artwork',
    dataUrl: o.dataUrl,
    side: o.side === 'back' ? 'back' : 'front',
    position: (o.position as ArtworkPositionId) || 'front-center',
    x: Number(o.x) || 338,
    y: Number(o.y) || 1570,
    w: Number(o.w) || 200,
    h: Number(o.h) || 200,
    rotation: Number(o.rotation) || 0,
    opacity: o.opacity === undefined ? 1 : Math.max(0, Math.min(1, Number(o.opacity))),
    mirrorSleeves: o.mirrorSleeves === true,
  };
}

function parsePatternLayer(raw: unknown): PatternLayer | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Partial<PatternLayer>;
  if (typeof o.id !== 'string' || typeof o.src !== 'string' || typeof o.region !== 'string') return null;
  const overrides =
    o.colorOverrides && typeof o.colorOverrides === 'object' && !Array.isArray(o.colorOverrides)
      ? Object.fromEntries(
          Object.entries(o.colorOverrides as Record<string, unknown>).filter(
            (e): e is [string, string] => typeof e[1] === 'string',
          ),
        )
      : undefined;
  return {
    id: o.id,
    region: o.region as PatternRegionId,
    src: o.src,
    kind: o.kind === 'image' ? 'image' : 'svg',
    name: typeof o.name === 'string' ? o.name : 'Pattern',
    opacity: o.opacity === undefined ? 1 : Math.max(0, Math.min(1, Number(o.opacity))),
    scale: Number(o.scale) || 1,
    rotation: Number(o.rotation) || 0,
    offsetX: Number(o.offsetX) || 0,
    offsetY: Number(o.offsetY) || 0,
    visible: o.visible !== false,
    ...(overrides && Object.keys(overrides).length ? { colorOverrides: overrides } : {}),
  };
}

function parseTextStyles(raw: unknown): Pick<Personalisation, 'teamStyle' | 'nameStyle' | 'numberStyle'> {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Partial<Personalisation>;
  const pick = (s?: Partial<TextStyle>) => (s && typeof s === 'object' ? s : undefined);
  return {
    teamStyle: pick(o.teamStyle),
    nameStyle: pick(o.nameStyle),
    numberStyle: pick(o.numberStyle),
  };
}

export function safeParseDesign(raw: unknown): SerializedDesign | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Partial<SerializedDesign>;
  const version = o.v as number | undefined;
  if ((version !== 1 && version !== 2 && version !== 3) || typeof o.productId !== 'string') return null;
  if (!Array.isArray(o.colors)) return null;
  const sizeRaw = o.size as Partial<GarmentSize> | undefined;
  const styles = parseTextStyles(o.personalisation);
  return {
    v: 3,
    productId: o.productId,
    templateId: typeof o.templateId === 'string' ? o.templateId : null,
    colors: o.colors
      .filter(c => c && typeof c.id === 'string' && typeof c.hex === 'string')
      .map(c => ({
        id: c.id,
        name: typeof c.name === 'string' ? c.name : c.id,
        hex: c.hex,
      })),
    personalisation: {
      teamName: o.personalisation?.teamName ?? 'FELLOW',
      playerName: o.personalisation?.playerName ?? 'PLAYER',
      number: o.personalisation?.number ?? '07',
      ...styles,
    },
    size: {
      label: sizeRaw?.label ?? DEFAULT_SIZE.label,
      chestCm: Number(sizeRaw?.chestCm) || DEFAULT_SIZE.chestCm,
      lengthCm: Number(sizeRaw?.lengthCm) || DEFAULT_SIZE.lengthCm,
      sleeveCm: Number(sizeRaw?.sleeveCm) || DEFAULT_SIZE.sleeveCm,
    },
    artworks: Array.isArray(o.artworks)
      ? (o.artworks.map(parseArtwork).filter(Boolean) as Artwork[])
      : [],
    layers: Array.isArray(o.layers)
      ? (o.layers.map(parsePatternLayer).filter(Boolean) as PatternLayer[])
      : [],
    savedAt: typeof o.savedAt === 'string' ? o.savedAt : new Date().toISOString(),
  };
}

const LS_KEY = 'kit-studio:last-design';

export function saveLocalDesign(design: SerializedDesign) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(design));
  } catch {
    /* quota */
  }
}

export function loadLocalDesign(): SerializedDesign | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return safeParseDesign(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function encodeShareHash(design: SerializedDesign): string {
  const json = JSON.stringify(design);
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  bytes.forEach(b => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeShareHash(hash: string): SerializedDesign | null {
  try {
    const b64 = hash.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const bin = atob(b64 + pad);
    const bytes = Uint8Array.from(bin, ch => ch.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return safeParseDesign(JSON.parse(json));
  } catch {
    return null;
  }
}
